import { memo, ReactNode, useContext, useEffect, useMemo } from "react";
import { ProductSort } from "src/types/productSort.d";
import {
  NavigationItems,
  PageContentType,
  ShopType,
} from "src/types/graphql.d";
import { PLPType } from "src/types/PLPType.d";
import { Department } from "src/types/department";
import { ProductList } from "../ProductList";
import { Header } from "./components/Header";
import {
  ContentContainer,
  HeaderContainer,
} from "./ProductCategoryView.styled";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { PopularBrandsWrapper } from "../PopularBrands";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import algoliasearch, { SearchClient } from "algoliasearch";
import { InstantSearchServerState } from "react-instantsearch-core-v7";
import { CATEGORIES_FACET, CONSCIOUS_FACET } from "src/utils/algolia";
import getAllBrandsPageRoute from "src/utils/getAllBrandsPageRoute";
import { Locale } from "lib/Intl";
import { getGenderId, setGender } from "src/modules/page";
import { useDispatch, useSelector } from "react-redux";
import { Breadcrumb } from "src/organisms/Breadcrumb";
import { useBreakpoints } from "@otrium/core";
import { PageHead } from "src/molecules/PageHead";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import getDeepLink from "src/utils/getDeepLink";
import { useRouter } from "next/router";
import { AppCtx } from "src/contexts/app.context";

export type ProductsViewProps = {
  title?: ReactNode;
  labelFilter?: string;
  limit: number;
  slug?: string;
  department?: Department;
  hasCategoryFilter: boolean;
  excludedFilters: string[];
  defaultSorting?: ProductSort;
  source?: PageContentType | null;
  plpType?: PLPType;
  hideBreadcrumbs?: boolean;
  description?: string;
};

type ProductCategoryViewProps = {
  gender: string;
  slug: string;
  title?: string;
  indexName: string;
  locale: string;
  breadcrumb?: { title: string; slug?: string }[];
  categoryPageId: string;
  search: {
    appId: string;
    searchKey: string;
  };
  pageType: PLPType;
  serverState?: InstantSearchServerState;
  navigationItems?: NavigationItems | null;
  categoryId?: string;
};

function ProductCategoryViewComponent({
  gender,
  slug,
  title,
  indexName,
  locale,
  breadcrumb,
  categoryPageId,
  search,
  serverState,
  pageType,
  navigationItems,
  categoryId,
}: ProductCategoryViewProps): JSX.Element {
  const { setPageViewedProperties } = useContext(AppCtx);
  const { isDesktop } = useBreakpoints();
  const { i18n } = useLingui();

  const router = useRouter();
  const dispatch = useDispatch();
  const currentGender = useSelector(getGenderId);
  const searchClient = useMemo<SearchClient>(
    () => algoliasearch(search?.appId, search?.searchKey),
    [search?.appId, search?.searchKey]
  );
  const excludeFilters = useMemo(
    () => (isDesktop ? [CATEGORIES_FACET, CONSCIOUS_FACET] : [CONSCIOUS_FACET]),
    [isDesktop]
  );

  useEffect(() => {
    setPageViewedProperties({
      page_type: "category",
      category_name: slug,
      category_id: categoryId,
    });
  }, [setPageViewedProperties, slug, categoryId]);

  useEffect(() => {
    if (currentGender !== localeFix({ gender })) {
      dispatch(setGender(localeFix({ gender })));
    }
  }, [gender, currentGender, dispatch]);

  const viewAllink = getAllBrandsPageRoute({
    shopType: localeFix({ gender }) as ShopType,
    locale: locale as Locale,
  });

  return (
    <>
      <PageHead
        title={t(i18n)`Online Outlet | Otrium`}
        description={t(
          i18n
        )`Browse all ${slug} for ${currentGender} at Otrium. Shop stylish scarves, hats, bags, belts, and more with discounts of up to 70%. Complete your look!`}
        deeplink={getDeepLink({
          hostname: `product-category/${gender}/${slug}`,
          query: {
            cat_slug: router.query?.cat_slug,
            discount: router.query?.discount,
          },
        })}
        enableIndexation
      />
      <ContentContainer>
        <HeaderContainer>
          {breadcrumb && breadcrumb.length > 0 ? (
            <Breadcrumb gender={gender} data={breadcrumb} />
          ) : null}
          <Header
            slug={slug}
            shopType={localeFix({ gender }) as ShopType}
            title={title}
          />
        </HeaderContainer>
        <ProductList
          title={title}
          slug={slug}
          gender={gender}
          pageContentType={pageType}
          categoryPageId={categoryPageId}
          searchClient={searchClient}
          indexName={indexName}
          serverState={serverState}
          excludeFilters={excludeFilters}
          navigationItems={navigationItems}
        />
        <PopularBrandsWrapper
          genderId={gender}
          localeGenderId={localeFix({ gender })}
          excludeSlug={slug}
          viewAllLink={viewAllink}
          plpType={PLPType.CATEGORY_PLP}
        />
      </ContentContainer>
      <RRPFootnote />
    </>
  );
}

const ProductCategoryView = memo(ProductCategoryViewComponent);

export { ProductCategoryView };
